/**
 * 双盲揭示复盘：按 pairId / 环节列出双方卡片完整信息。
 *
 * 每张卡片展示：类型 / 标题 / 角色 / 必打论据 / 三步题链 / 沙盘大纲 /
 * 隐藏反杀锦囊，以及「承诺哈希 vs 揭示哈希」的校验状态。
 */
import { sideLabel } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { Badge, EmptyState } from '../ui/primitives.tsx';
import {
  describeRevealVerification,
  formatTimeOfDay,
  shortHash,
  summarizeRevealedCard,
} from '../../export/report.ts';

function verificationTone(verified: boolean | null): 'pass' | 'violation' | 'warning' {
  if (verified === true) return 'pass';
  if (verified === false) return 'violation';
  return 'warning';
}

export function RevealReview() {
  const { state } = useArena();

  const groups = new Map<string, typeof state.reveals>();
  for (const reveal of state.reveals) {
    const key = reveal.pairId || `stage-${reveal.stageIndex}`;
    const list = groups.get(key) ?? [];
    list.push(reveal);
    groups.set(key, list);
  }
  const orderedGroups = [...groups.entries()]
    .map(([pairId, reveals]) => ({
      pairId,
      reveals: [...reveals].sort(
        (a, b) => a.stageIndex - b.stageIndex || (a.side === 'AFF' ? -1 : 1),
      ),
    }))
    .sort((a, b) => (a.reveals[0]?.stageIndex ?? 0) - (b.reveals[0]?.stageIndex ?? 0));

  return (
    <section className="rounded-xl border border-double-blind/30 bg-bg-surface/80 backdrop-blur">
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-white/5 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-slate-100">双盲揭示复盘</h2>
          <p className="mt-0.5 text-xs text-slate-400">
            已揭示 {state.reveals.length} 张 · 覆盖 {orderedGroups.length} 个配对 · Commit-Reveal 校验
          </p>
        </div>
        <Badge tone="blind">卡牌已全盘亮明</Badge>
      </header>
      <div className="space-y-3 p-3">
        {state.reveals.length === 0 ? (
          <EmptyState
            title="尚未揭示任何底牌"
            detail="比赛开始并结束一个双盲配对（或教师强制揭示）后，双方卡片会在这里全盘亮明。"
          />
        ) : (
          orderedGroups.map((group) => {
            const stageIndices = [...new Set(group.reveals.map((reveal) => reveal.stageIndex))].sort(
              (a, b) => a - b,
            );
            return (
              <div key={group.pairId}>
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <span className="mono text-xs font-semibold text-double-blind">
                    配对 {group.pairId}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    环节 {stageIndices.join('、')} · 揭示 {group.reveals.length} 张
                  </span>
                </div>
                <div className="grid gap-2 lg:grid-cols-2">
                  {group.reveals.map((reveal) => {
                    const summary = summarizeRevealedCard(reveal);
                    const commitment = state.commitments[reveal.pairId]?.[reveal.side] ?? null;
                    const verification = describeRevealVerification(reveal, commitment);
                    return (
                      <article
                        key={`${reveal.pairId}:${reveal.stageIndex}:${reveal.side}`}
                        data-testid="review-reveal-card"
                        className="rounded-lg border border-white/10 bg-black/25 p-3"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-1">
                          <div className="flex items-center gap-1.5">
                            <Badge tone={reveal.side === 'AFF' ? 'aff' : 'neg'}>
                              {sideLabel(reveal.side)} · 环节 {reveal.stageIndex}
                            </Badge>
                            <Badge tone="blind">{summary.kindLabel}</Badge>
                          </div>
                          <Badge tone={verificationTone(verification.verified)}>
                            {verification.label}
                          </Badge>
                        </div>
                        <h3 className="mt-1.5 text-sm font-semibold text-slate-100">
                          {summary.title}
                        </h3>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                          {summary.targetRole && <span>角色：{summary.targetRole}</span>}
                          {summary.archetypeLabel && <span>流派：{summary.archetypeLabel}</span>}
                          {summary.modeLabel && <span>模式：{summary.modeLabel}</span>}
                          <span className="mono">揭示于 {formatTimeOfDay(reveal.revealedAt)}</span>
                        </div>
                        {summary.coreMission && (
                          <p className="mt-1.5 rounded bg-black/25 px-2 py-1 text-xs text-slate-300">
                            核心指令：{summary.coreMission}
                          </p>
                        )}
                        {summary.questionChain && (
                          <div className="mt-1.5">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">
                              三步题链
                            </div>
                            <ol className="mt-0.5 space-y-0.5 text-[11px] text-slate-300">
                              <li>① 锁前提：{summary.questionChain.step1LockPremise}</li>
                              <li>② 逼两难：{summary.questionChain.step2ForceDilemma}</li>
                              <li>③ 夺判准：{summary.questionChain.step3Crystallize}</li>
                            </ol>
                          </div>
                        )}
                        {summary.sandbox && (
                          <div className="mt-1.5">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">
                              沙盘大纲 · {summary.sandbox.topicLabel}
                            </div>
                            <p className="mt-0.5 text-[11px] text-double-blind">
                              {summary.sandbox.label}
                            </p>
                            <p className="text-[11px] text-slate-500">
                              焦点：{summary.sandbox.crossfireQuestion}
                            </p>
                            <ol className="mt-0.5 space-y-0.5 text-[11px] text-slate-300">
                              {summary.sandbox.outline.map((turn) => (
                                <li key={turn.turn}>
                                  回合 {turn.turn} · {turn.header}：{turn.keyClaim}
                                  {turn.counterToOpponent && (
                                    <span className="block text-[10px] text-slate-500">
                                      预判反制：{turn.counterToOpponent}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}
                        {summary.evidenceTitles.length > 0 && (
                          <div className="mt-1.5">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">
                              必打论据
                            </div>
                            <ul className="mt-0.5 space-y-0.5">
                              {summary.evidenceTitles.map((title, index) => (
                                <li key={`${title}-${index}`} className="text-[11px] text-slate-300">
                                  {title}
                                  <span className="block text-[10px] text-slate-500">
                                    {summary.evidenceQuotes[index]}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {summary.hiddenTrap && (
                          <details className="mt-1.5 rounded bg-double-blind/[0.07] px-2 py-1">
                            <summary className="cursor-pointer text-[11px] text-double-blind">
                              ⚡ 隐藏反杀锦囊
                            </summary>
                            <p className="mt-0.5 text-[11px] text-slate-300">
                              触发：{summary.hiddenTrap.triggerCondition}
                            </p>
                            <p className="text-[11px] text-slate-300">
                              反制：{summary.hiddenTrap.counterResponse}
                            </p>
                          </details>
                        )}
                        {summary.tabooWarning && (
                          <p className="mt-1 text-[11px] text-assert-violation">
                            禁忌：{summary.tabooWarning}
                          </p>
                        )}
                        {summary.intelHint && (
                          <p className="mt-1 text-[11px] text-slate-500">
                            情报提示：{summary.intelHint}
                          </p>
                        )}
                        <div className="mono mt-1.5 truncate text-[10px] text-slate-600">
                          hash {shortHash(reveal.hash, 24)} · salt {shortHash(reveal.salt, 12)}
                        </div>
                        <p className="mt-0.5 text-[10px] text-slate-500" data-testid="reveal-verification-detail">
                          {verification.detail}
                        </p>
                      </article>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
